/**
 * Waits for the auth-gated Firestore listener to populate `signedUpPatient` in the store.
 */

import type { SignedUpPatient } from '@physiofit-digital/physiofit_types';
import { useOnboardingStore } from '../store/onboardingStore';

/** After this long without a server answer, `useSignedUpPatientConnect` reports `error`. */
export const SIGNED_UP_PATIENT_LOAD_TIMEOUT_MS = 15_000;

/** `unavailable`: the listener failed or no data arrived in time, so the invitation may still exist. */
export type SignedUpPatientReadyResult =
  { status: 'ready'; patient: SignedUpPatient } | { status: 'not_found' } | { status: 'unavailable' };

/**
 * Resolves when `signedUpPatientStatus` is terminal for the given `signUpId`, or after timeout.
 */
export function waitForSignedUpPatientReady(
  signUpId: string,
  timeoutMs = SIGNED_UP_PATIENT_LOAD_TIMEOUT_MS,
): Promise<SignedUpPatientReadyResult> {
  const trimmedId = signUpId.trim();
  const initial = useOnboardingStore.getState();
  if (initial.signUpId === trimmedId && initial.signedUpPatientStatus === 'ready' && initial.signedUpPatient) {
    return Promise.resolve({ status: 'ready', patient: initial.signedUpPatient });
  }
  if (initial.signUpId === trimmedId && initial.signedUpPatientStatus === 'not_found') {
    return Promise.resolve({ status: 'not_found' });
  }
  if (initial.signUpId === trimmedId && initial.signedUpPatientStatus === 'error') {
    return Promise.resolve({ status: 'unavailable' });
  }

  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => {
      unsubscribe();
      resolve({ status: 'unavailable' });
    }, timeoutMs);

    const unsubscribe = useOnboardingStore.subscribe((state) => {
      if (state.signUpId !== trimmedId) {
        return;
      }
      if (state.signedUpPatientStatus === 'ready' && state.signedUpPatient) {
        window.clearTimeout(timeoutId);
        unsubscribe();
        resolve({ status: 'ready', patient: state.signedUpPatient });
        return;
      }
      if (state.signedUpPatientStatus === 'not_found' || state.signedUpPatientStatus === 'error') {
        window.clearTimeout(timeoutId);
        unsubscribe();
        resolve({ status: state.signedUpPatientStatus === 'not_found' ? 'not_found' : 'unavailable' });
      }
    });
  });
}
